import React from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StatusChip } from 'src/components/payments-ops/StatusChip'
import { Button } from 'src/components/shared/Button'
import { paymentReviewReasonLabels } from 'src/constants/payments-ops/labels'
import type { PaymentReviewReason } from 'src/constants/payments-ops/enums'
import type { PendingPayment } from 'src/interfaces/paymentsReview'
import { formatDateTime, parseUnixTimestamp } from 'src/utils/grid'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { formatDriverName } from 'src/utils/paymentsOps/pendingPayments'

export const buildPendingPaymentColumns = (
  onReview: (payment: PendingPayment) => void,
  isSubmitting: boolean,
): Array<GridColDef<PendingPayment>> => {
  return [
    {
      field: 'id',
      headerName: 'Payment ID',
      width: 150,
      renderCell: (params: GridRenderCellParams<PendingPayment, string>) => {
        return <span css={tw`truncate font-mono text-xs`}>{params.value}</span>
      },
    },
    { field: 'partner', headerName: 'Partner', flex: 1, minWidth: 160 },
    {
      field: 'partnerDriverName',
      headerName: 'Driver',
      flex: 1,
      minWidth: 220,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PendingPayment, string>) => {
        return (
          <div css={tw`flex min-w-0 flex-col`}>
            <span css={tw`truncate`}>{formatDriverName(params.row.partnerDriverName)}</span>
            <Typography variant="body2" css={tw`truncate text-xs text-shades-500`}>
              {params.row.partnerDriverEmail}
            </Typography>
          </div>
        )
      },
    },
    { field: 'partnerDriverPhone', headerName: 'Phone', width: 150 },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) => {
        return formatCentsAsUsd(value)
      },
    },
    {
      field: 'reviewReason',
      headerName: 'Review reason',
      width: 170,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PendingPayment, PaymentReviewReason | null>) => {
        if (!params.value) {
          return <span>—</span>
        }

        const reason = paymentReviewReasonLabels[params.value]

        return <StatusChip label={reason?.label ?? params.value} tone={reason?.tone ?? 'neutral'} />
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created at',
      width: 190,
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateTime,
    },
    {
      field: 'actions',
      headerName: '',
      width: 110,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<PendingPayment>) => {
        return (
          <Button
            size="small"
            color="white"
            disabled={isSubmitting}
            onClick={() => {
              onReview(params.row)
            }}
            data-testid={`payments-review-review-${params.row.id}`}
          >
            Review
          </Button>
        )
      },
    },
  ]
}
